<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Mail, ArrowLeft } from 'lucide-vue-next';
import { authAPI } from '../services/api';

const router = useRouter();
const { t } = useI18n();
const email = ref('');
const isLoading = ref(false);
const isSuccess = ref(false);
const error = ref('');

const handleSubmit = async (e: Event) => {
  e.preventDefault();
  if (!email.value) return;

  isLoading.value = true;
  error.value = '';

  try {
    await authAPI.resetPassword({ email: email.value });
    isSuccess.value = true;
  } catch (err: any) {
    error.value = err.response?.data?.message || t('common.error_generic');
  } finally {
    isLoading.value = false;
  }
};

const goBack = () => {
  router.push('/login');
};
</script>

<template>
  <div class="flex min-h-screen bg-[#0A0A0B] items-center justify-center p-6">
    <div class="w-full max-w-md">
      <!-- Logo/Brand Section -->
      <div class="text-center mb-8">
        <img
          src="@/assets/images/logo-dark.png"
          alt="Assistly"
          class="w-20 h-20 object-contain mb-4"
        />
        <h2 class="text-3xl text-[#F5F0EA] mb-2">Assistly</h2>
        <p class="text-[#9E9080]">{{ $t('reset_password.page_subtitle') }}</p>
      </div>

      <!-- Reset Password Form -->
      <div class="w-full bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10">
        <div v-if="!isSuccess">
          <!-- Header -->
          <div class="mb-8 text-center">
            <h1 class="text-2xl text-[#F5F0EA] mb-2">
              {{ $t('reset_password.title') }}
            </h1>
            <p class="text-[#9E9080]">{{ $t('reset_password.subtitle') }}</p>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="mb-5 p-4 bg-red-900/20 border border-red-800/40 rounded-2xl">
            <p class="text-sm text-red-400">{{ error }}</p>
          </div>

          <!-- Form -->
          <form @submit="handleSubmit" class="space-y-5">
            <!-- Email Field -->
            <div>
              <label for="email" class="block text-sm text-[#F5F0EA] mb-2">{{
                $t('reset_password.email_label')
              }}</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail class="w-4 h-4 text-[#524840]" />
                </div>
                <input
                  type="email"
                  id="email"
                  required
                  v-model="email"
                  class="w-full pl-11 pr-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#1E1E23] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840]"
                  :placeholder="$t('reset_password.email_placeholder')"
                />
              </div>
            </div>

            <!-- Buttons -->
            <div class="space-y-3">
              <button
                type="submit"
                :disabled="!email || isLoading"
                class="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#D4956A] text-[#0A0A0B] rounded-2xl hover:bg-[#E8B48A] disabled:bg-[#18181C] disabled:text-[#524840] disabled:cursor-not-allowed transition font-medium"
              >
                <span v-if="isLoading">{{ $t('reset_password.submitting') }}</span>
                <span v-else>{{ $t('reset_password.submit') }}</span>
              </button>

              <button
                type="button"
                @click="goBack"
                class="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#18181C] text-[#9E9080] rounded-2xl hover:bg-[#1E1E23] transition"
              >
                <ArrowLeft class="w-4 h-4" />
                {{ $t('common.back_to_login') }}
              </button>
            </div>
          </form>
        </div>

        <!-- Success Message -->
        <div v-else class="text-center py-8">
          <div
            class="w-16 h-16 bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <svg
              class="w-8 h-8 text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 class="text-xl text-[#F5F0EA] mb-2">
            {{ $t('reset_password.success_title') }}
          </h2>
          <p class="text-[#9E9080] mb-6">
            {{ $t('reset_password.success_message') }}
          </p>
          <button
            @click="goBack"
            class="inline-flex items-center gap-2 px-5 py-3 bg-[#D4956A] text-[#0A0A0B] rounded-2xl hover:bg-[#E8B48A] transition font-medium"
          >
            <ArrowLeft class="w-4 h-4" />
            {{ $t('common.back_to_login') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
